import { React } from 'Imports';
import { isEncompass } from '$Utilities/integrationPlatform';
import { useEffect, useRef, useState } from 'react';
import { DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { CameraSegmentBase, CameraSegmentFullData } from '../TimeAndDurationStep/FootageSegments';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import ProgressBar from './ProgressBar';
import VideoPreview from './VideoPreview';
import VideoPlayerSyncProvider from '$State/VideoPlayerSync/VideoPlayerSyncProvider';

const styles = require('./FootageSegmentPreview.scss') as {
    geotab: string;
    encompass: string;
    mainContainer: string;
    loadingContainer: string;
    errorContainer: string;
    textLink: string;
};

interface FootageSegmentPreviewProps {
    cameraSegment: CameraSegmentFullData;
    fleetTimezone: string | null;
    recallStartDate?: Date;
    selectedDuration: number | undefined;
    durationOptions: IDropdownItem[];
    onSegmentChange: (segment: CameraSegmentBase) => void;
    onRecallStartDateChange: (date: Date | undefined) => void;
    onDurationChange: (duration: number | undefined) => void;
    // Reports whether the footage has finished loading and the player is showing, so the step can hold
    // back content that only makes sense alongside a usable preview (the timezone conversion banner).
    onPreviewReadyChange?: (isReady: boolean) => void;
    onValidationErrorChange?: (hasError: boolean) => void;
    // Reports whether the camera has gone offline while this preview was loading, so the header's
    // online/offline tag (sourced from a snapshot fetched before polling began) can be corrected.
    onCameraDisconnectedChange?: (isDisconnected: boolean) => void;
}

type ViewState = 'loading' | 'preview' | 'error';

const POLL_INTERVAL_MS = 5000;
const MAX_TRANSIENT_RETRIES = 6;

const FootageSegmentPreview = (props: FootageSegmentPreviewProps) => {
    const { cameraSegment } = props;

    const [viewState, setViewState] = useState<ViewState>('loading');
    const [loadingProgress, setLoadingProgress] = useState<number | null>(null);
    const [previewId, setPreviewId] = useState<number | null>(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [retryKey, setRetryKey] = useState(0);
    const [pollKey, setPollKey] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const forceNewRequestRef = useRef(false);
    const transientErrorCountRef = useRef(0);
    const [recoverableFailure, setRecoverableFailure] = useState<boolean | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const integrationClass = isEncompass ? styles.encompass : styles.geotab;

    // Effect 1: get or create the preview request ID
    useEffect(() => {
        if (!cameraSegment) return;

        let cancelled = false;

        const init = async () => {
            setPollKey((k) => k + 1);
            setPreviewId(null);
            setViewState('loading');
            setLoadingProgress(null);
            setRecoverableFailure(null);
            props.onCameraDisconnectedChange?.(false);

            const forceNew = forceNewRequestRef.current;
            forceNewRequestRef.current = false;
            transientErrorCountRef.current = 0;

            if (cameraSegment.existingRequestId && !forceNew) {
                setPreviewId(cameraSegment.existingRequestId);
                return;
            }

            try {
                await DeviceAssociationService.requestVideoRecallPreview({
                    serialNumber: cameraSegment.serialNumber,
                    channelId: 1,
                    startTime: cameraSegment.startTime,
                    endTime: cameraSegment.endTime,
                });
                if (cancelled) return;
                const id = DeviceAssociationService.getState().videoRecallPreview.data;
                if (id) {
                    setPreviewId(id);
                } else {
                    setViewState('error');
                }
            } catch (error) {
                if (!cancelled) {
                    setViewState('error');
                    setErrorMessage('Service is unavailable at this time, please try again later.');
                }
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, [cameraSegment, retryKey]);

    useEffect(() => {
        props.onPreviewReadyChange?.(viewState === 'preview');
    }, [viewState]);

    // Effect 2: poll progress once we have a previewId
    useEffect(() => {
        if (!previewId) return;

        const checkProgress = async () => {
            try {
                await DeviceAssociationService.getFootageSegmentsProgress(previewId);
                if (DeviceAssociationService.getState().footageSegmentsProgress.error) {
                    setViewState('error');
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    return;
                }
                const result = DeviceAssociationService.getState().footageSegmentsProgress.data;
                if (!result) return;

                transientErrorCountRef.current = 0;

                if (result.isReady) {
                    setFileUrl(result.fileUrl ?? null);
                    setViewState('preview');

                    if (intervalRef.current) clearInterval(intervalRef.current);
                } else if (result.failed) {
                    const isRecoverable = result.failureReason?.indexOf('Try Again Later.') !== -1;
                    const isCameraDisconnected = result.failureReason?.indexOf('Camera Disconnected') !== -1;

                    if (isRecoverable) {
                        forceNewRequestRef.current = true;
                    } else if (isCameraDisconnected) {
                        setErrorMessage('Preview footage not available due to camera disconnection.');
                        props.onCameraDisconnectedChange?.(true);
                    } else {
                        setErrorMessage('Preview not available due to unexpected error.');
                    }

                    setRecoverableFailure(isRecoverable);
                    setViewState('error');
                    if (intervalRef.current) clearInterval(intervalRef.current);
                } else {
                    setLoadingProgress(Math.round(result.percent ?? 0));
                }
            } catch (error) {
                transientErrorCountRef.current++;
                if (transientErrorCountRef.current >= MAX_TRANSIENT_RETRIES) {
                    setErrorMessage('Service is unavailable at this time, please try again later.');
                    setViewState('error');
                    if (intervalRef.current) clearInterval(intervalRef.current);
                }
            }
        };

        checkProgress();
        intervalRef.current = setInterval(checkProgress, POLL_INTERVAL_MS);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [previewId, pollKey]);

    const ErrorBanner = () => (
        <div className={styles.errorContainer}>
            {recoverableFailure ? (
                <span>
                    Loading unexpectedly failed.{' '}
                    <span className={styles.textLink} onClick={() => setRetryKey((k) => k + 1)}>
                        Try again
                    </span>{' '}
                    later.
                </span>
            ) : (
                <span>{errorMessage}</span>
            )}
        </div>
    );

    const renderContent = () => {
        switch (viewState) {
            case 'loading':
                return (
                    <div className={styles.loadingContainer}>
                        <ProgressBar loadingProgress={loadingProgress} />
                    </div>
                );
            case 'preview':
                return (
                    <div className={styles.mainContainer}>
                        <VideoPlayerSyncProvider isFullView={false}>
                            <VideoPreview
                                fileUrl={fileUrl}
                                cameraSegment={cameraSegment}
                                fleetTimezone={props.fleetTimezone}
                                recallStartDate={props.recallStartDate}
                                selectedDuration={props.selectedDuration}
                                durationOptions={props.durationOptions}
                                onSegmentChange={props.onSegmentChange}
                                onRecallStartDateChange={props.onRecallStartDateChange}
                                onDurationChange={props.onDurationChange}
                                onValidationErrorChange={props.onValidationErrorChange}
                            />
                        </VideoPlayerSyncProvider>
                    </div>
                );
            case 'error':
                return <ErrorBanner />;
            default:
                return null;
        }
    };

    return <div className={integrationClass}>{renderContent()}</div>;
};

export default FootageSegmentPreview;
